import React, { useState, useEffect } from 'react';
import { 
  History, Search, Filter, Download, RefreshCw, 
  User, Calendar, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import AdminLogsTabs from './AdminLogsTabs';

const AdminActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    admin_id: '',
    action: '',
    from_date: '',
    to_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  
  const { api } = useAdminAuth();

  const parseActivityTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp instanceof Date) return timestamp;

    const value = String(timestamp).trim();
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    const isoLike = /^\d{4}-\d{2}-\d{2}T/.test(value);
    const normalized = isoLike && !hasTimezone ? `${value}Z` : value;
    const date = new Date(normalized);

    return Number.isNaN(date.getTime()) ? null : date;
  };

  const formatActivityTimestamp = (timestamp) => {
    const date = parseActivityTimestamp(timestamp);
    return date ? date.toLocaleString() : '-';
  };

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('skip', (page - 1) * limit);
      
      if (filters.admin_id) params.append('admin_id', filters.admin_id);
      if (filters.action) params.append('action', filters.action);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      
      const response = await api.get(`/api/admin/activity-logs?${params}`);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchLogs();
  };

  const handleReset = () => {
    setFilters({ admin_id: '', action: '', from_date: '', to_date: '' });
    setPage(1);
    setTimeout(fetchLogs, 100);
  };

  const handleExport = () => {
    // Create CSV
    const headers = ['Timestamp', 'User Name', 'User ID', 'Action', 'Details'];
    const rows = logs.map(log => [
      parseActivityTimestamp(log.timestamp)?.toISOString() || '',
      getUserName(log),
      log.admin_id,
      log.action,
      formatDetailsForExport(log)
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Activity logs exported');
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const date = parseActivityTimestamp(timestamp);
    if (!date) return '-';
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getActionColor = (action) => {
    if (action.includes('login')) return 'bg-blue-100 text-blue-700';
    if (action.includes('create') || action.includes('add')) return 'bg-green-100 text-green-700';
    if (action.includes('update') || action.includes('edit')) return 'bg-amber-100 text-amber-700';
    if (action.includes('delete') || action.includes('remove')) return 'bg-red-100 text-red-700';
    if (action.includes('restrict') || action.includes('deactivate')) return 'bg-red-100 text-red-700';
    if (action.includes('invite') || action.includes('team')) return 'bg-purple-100 text-purple-700';
    if (action.includes('payment') || action.includes('confirm')) return 'bg-emerald-100 text-emerald-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getActionIcon = (action) => {
    if (action.includes('login')) return '🔐';
    if (action.includes('member')) return '👤';
    if (action.includes('payment')) return '💳';
    if (action.includes('tier')) return '⭐';
    if (action.includes('team') || action.includes('invite')) return '👥';
    if (action.includes('template')) return '📧';
    if (action.includes('restrict')) return '🚫';
    if (action.includes('note')) return '📝';
    if (action.includes('target') || action.includes('prospect')) return '🎯';
    if (action.includes('event')) return '📅';
    if (action.includes('setting')) return '⚙️';
    return '📋';
  };

  const formatAction = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getUserName = (log) => {
    return log.user_name || log.admin_name || log.admin_full_name || '';
  };

  const formatDetailValue = (log, key, value) => {
    const displayValue = log.details_display?.[key];
    if (displayValue) return displayValue;
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  };

  const formatDetailsForExport = (log) => {
    const details = log.details || {};
    const displayDetails = Object.entries(details).reduce((acc, [key, value]) => {
      acc[key] = formatDetailValue(log, key, value);
      return acc;
    }, {});
    return JSON.stringify(displayDetails);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="admin-activity-logs">
      <AdminLogsTabs active="activity" />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activity Logs</h1>
          <p className="text-gray-500">Audit trail of all admin activities</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} className="mr-2" />
            Filters
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download size={16} className="mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={fetchLogs}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Admin ID</Label>
                <Input
                  value={filters.admin_id}
                  onChange={(e) => setFilters({ ...filters, admin_id: e.target.value })}
                  placeholder="ADM-XXXXXXXX"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Action</Label>
                <Input
                  value={filters.action}
                  onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                  placeholder="Search actions..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={filters.from_date}
                  onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={filters.to_date}
                  onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <Button variant="outline" onClick={handleReset}>Reset</Button>
              <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Search size={16} className="mr-2" />
                Search
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Logs</p>
            <p className="text-2xl font-bold">{total.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Today's Activity</p>
            <p className="text-2xl font-bold">
              {logs.filter(l => {
                const today = new Date().toDateString();
                return parseActivityTimestamp(l.timestamp)?.toDateString() === today;
              }).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Unique Admins</p>
            <p className="text-2xl font-bold">
              {new Set(logs.map(l => l.admin_id)).size}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Action Types</p>
            <p className="text-2xl font-bold">
              {new Set(logs.map(l => l.action)).size}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <History size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No activity logs found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.map((log, idx) => (
                  <tr key={log.log_id || idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <Calendar size={14} className="text-gray-400" />
                        <div>
                          <p className="text-sm font-medium">{formatTimeAgo(log.timestamp)}</p>
                          <p className="text-xs text-gray-500">
                            {formatActivityTimestamp(log.timestamp)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User size={14} className="text-blue-600" />
                        </div>
                        <div>
                          {getUserName(log) && (
                            <p className="text-sm font-medium text-gray-900">{getUserName(log)}</p>
                          )}
                          <p className="font-mono text-xs text-gray-500">{log.admin_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{getActionIcon(log.action)}</span>
                        <Badge className={getActionColor(log.action)}>
                          {formatAction(log.action)}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {log.details && Object.keys(log.details).length > 0 ? (
                        <div className="text-sm text-gray-600 max-w-md">
                          {Object.entries(log.details).slice(0, 3).map(([key, value]) => (
                            <div key={key} className="break-words">
                              <span className="text-gray-400">{key}:</span>{' '}
                              <span className="break-all" title={formatDetailValue(log, key, value)}>
                                {formatDetailValue(log, key, value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <p className="text-sm text-gray-500">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} logs
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft size={16} />
                  Previous
                </Button>
                <div className="flex items-center px-3">
                  Page {page} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminActivityLogs;
